import React from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Send, ChevronLeft, ChevronRight, Loader2, ArrowLeft } from 'lucide-react';

export default function ReportRequestWizard({
  onBack, requestStep, setRequestStep, requestData, setRequestData,
  selectedParticipants, setSelectedParticipants, participants, teamMembers,
  onSend, loading
}) {
  const requestSteps = ['Period & Due Date', 'Select Participants', 'Message & Preview', 'Confirm & Send'];

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto space-y-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack}><ArrowLeft className="h-4 w-4" /></Button>
          <h1 className="text-xl font-bold text-slate-900">Request Periodic Reports</h1>
        </div>

        {/* Step progress */}
        <div className="flex gap-1">{requestSteps.map((s, i) => (
          <div key={i} className={`flex-1 h-1.5 rounded-full ${i <= requestStep ? 'bg-cyan-600' : 'bg-slate-200'}`} />
        ))}</div>
        <p className="text-sm text-slate-500">Step {requestStep + 1}: {requestSteps[requestStep]}</p>

        <Card>
          <CardContent className="p-5 space-y-4">
            {requestStep === 0 && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div><Label className="text-xs">Period Start</Label><Input type="date" value={requestData.period_start} onChange={e => setRequestData({...requestData, period_start: e.target.value})} data-testid="req-period-start" /></div>
                  <div><Label className="text-xs">Period End</Label><Input type="date" value={requestData.period_end} onChange={e => setRequestData({...requestData, period_end: e.target.value})} data-testid="req-period-end" /></div>
                  <div><Label className="text-xs">Due Date</Label><Input type="date" value={requestData.due_date} onChange={e => setRequestData({...requestData, due_date: e.target.value})} data-testid="req-due-date" /></div>
                </div>
                <div><Label className="text-xs">Participant Type</Label>
                  <Select value={requestData.participant_type_filter} onValueChange={v => setRequestData({...requestData, participant_type_filter: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Participants</SelectItem>
                      <SelectItem value="consultant">Consultants Only</SelectItem>
                      <SelectItem value="subcontractor">Subcontractors Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            {requestStep === 1 && (
              <>
                <p className="text-xs text-slate-500">Select which consultants/subcontractors should receive a report request.</p>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {(() => {
                    const allRecipients = [];
                    const seenIds = new Set();
                    participants.forEach(p => {
                      if (!seenIds.has(p.user_id)) {
                        seenIds.add(p.user_id);
                        allRecipients.push(p);
                      }
                    });
                    teamMembers.forEach(m => {
                      const isProfRole = (m.role || '').startsWith('prof_') || ['professional_team', 'consultant', 'principal_agent'].includes(m.role);
                      if (!seenIds.has(m.id) && (isProfRole || m.role === 'subcontractor')) {
                        seenIds.add(m.id);
                        allRecipients.push({
                          user_id: m.id,
                          user_name: m.name || m.email,
                          company_name: m.company || m.company_name || '',
                          discipline: m.discipline || m.role,
                          participant_type: m.role === 'subcontractor' ? 'subcontractor' : 'consultant'
                        });
                      }
                    });
                    const filtered = allRecipients.filter(p => requestData.participant_type_filter === 'all' || p.participant_type === requestData.participant_type_filter);
                    if (filtered.length === 0) {
                      return <p className="text-sm text-slate-400 text-center py-8">No consultants or subcontractors found. Invite team members first via User Management.</p>;
                    }
                    return filtered.map(p => (
                      <label key={p.user_id} className={`flex items-center gap-3 border rounded-lg p-3 cursor-pointer transition-colors ${selectedParticipants.has(p.user_id) ? 'bg-cyan-50 border-cyan-300' : 'hover:bg-slate-50'}`}>
                        <input type="checkbox" checked={selectedParticipants.has(p.user_id)} onChange={e => {
                          const next = new Set(selectedParticipants);
                          e.target.checked ? next.add(p.user_id) : next.delete(p.user_id);
                          setSelectedParticipants(next);
                        }} className="rounded" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-800 truncate">{p.user_name}</p>
                          <p className="text-xs text-slate-500 truncate">{p.company_name}{p.discipline ? ` — ${p.discipline}` : ''}</p>
                        </div>
                        <Badge className={`text-[10px] flex-shrink-0 ${p.participant_type === 'consultant' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>{p.participant_type}</Badge>
                      </label>
                    ));
                  })()}
                </div>
              </>
            )}

            {requestStep === 2 && (
              <>
                <div><Label className="text-xs">Instruction Message (Optional)</Label>
                  <Textarea value={requestData.instruction_message} onChange={e => setRequestData({...requestData, instruction_message: e.target.value})} placeholder="Any specific instructions for this reporting period..." rows={4} />
                </div>
                <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                  <p className="text-xs font-semibold text-slate-700">Preview</p>
                  <p className="text-xs text-slate-600">Period: {requestData.period_start} to {requestData.period_end}</p>
                  <p className="text-xs text-slate-600">Due: {requestData.due_date}</p>
                  <p className="text-xs text-slate-600">Recipients: {selectedParticipants.size > 0 ? `${selectedParticipants.size} selected` : 'None selected — please go back and select recipients'}</p>
                </div>
              </>
            )}

            {requestStep === 3 && (
              <div className="text-center space-y-4 py-4">
                <Send className="h-12 w-12 mx-auto text-cyan-600" />
                <h3 className="text-lg font-semibold text-slate-800">Ready to Send</h3>
                <p className="text-sm text-slate-500">Report requests will be sent via email and appear in each participant's portal.</p>
                <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4 text-sm text-left space-y-1">
                  <p><strong>Period:</strong> {requestData.period_start} to {requestData.period_end}</p>
                  <p><strong>Due Date:</strong> {requestData.due_date}</p>
                  <p><strong>Recipients:</strong> {selectedParticipants.size > 0 ? selectedParticipants.size : 0}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex justify-between">
          <Button variant="outline" onClick={() => requestStep > 0 ? setRequestStep(requestStep - 1) : onBack()}>
            <ChevronLeft className="h-4 w-4 mr-1" /> {requestStep > 0 ? 'Back' : 'Cancel'}
          </Button>
          {requestStep < 3 ? (
            <Button onClick={() => setRequestStep(requestStep + 1)}><ChevronRight className="h-4 w-4 ml-1" /> Next</Button>
          ) : (
            <Button onClick={onSend} disabled={loading} className="bg-cyan-600 hover:bg-cyan-700" data-testid="send-requests-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />} Send Requests
            </Button>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
